/**
 * Creates a single-consumer async channel.
 * Simpler than Broadcast - only supports one consumer.
 *
 * @param initial - Optional array of initial messages
 * @returns Object with push, stop, and stream
 *
 * @example
 * ```ts
 * import { Channel } from "@pdfking/tfunc"
 *
 * const { push, stop, stream } = Channel<string>()
 *
 * // Push messages
 * push("hello")
 * push("world")
 *
 * // Stop the channel
 * stop(undefined)
 *
 * // Consume messages
 * for await (const msg of stream) {
 *   console.log(msg) // Outputs: "hello", "world"
 * }
 * ```
 */
export const Channel = <T>(
  initial?: T[]
): {
  push: (item: T) => void
  stop: (error: Error | undefined) => void
  stream: AsyncGenerator<T>
} => {
  let is_completed = false
  let error: Error | undefined
  let callback: ((v: true) => void) | undefined
  const buffer: T[] = initial ? [...initial] : []

  const drain = () => buffer.splice(0, buffer.length)

  const generator = async function* (): AsyncGenerator<T> {
    while (!is_completed) {
      const promise = new Promise<true>(resolve => {
        callback = resolve
      })
      yield* drain()
      await promise
    }

    yield* drain()

    if (error) {
      throw error
    }
  }

  return {
    push(item: T): void {
      buffer.push(item)
      callback?.(true)
    },

    stop(e: Error | undefined): void {
      if (e) error = e
      is_completed = true
      callback?.(true)
    },

    stream: generator(),
  }
}
